/** 持仓 —— 这本账持有什么（已平仓的也留着）、按权益口径每个仓位值多少、这个仓位是哪条信号带来的。
 *
 * 规格 `docs/design/screens/positions.md`。口径（trade-attribution「口径唯一」、裁决 D12）：
 * 账户 KPI 以**权益口径**为主（日报 / 权益序列 / 运行归档三个出处之一），现金变动只作对照；
 * **不把各仓位 `realized_pnl` 加总当账户盈亏**——仓位行的已实现是内核仓位事件原值，只作对照。
 * 标记价 / 市值 / 未实现只取后端 `/positions` 给的值，缺就显示「缺」，不拿开仓均价顶替。
 */

import { useState } from "react";

import {
  basisFromRun,
  useDailyReportBasis,
  useDecisionsBook,
  useEquityBasis,
  useFillsBook,
  usePositionsBook,
  useRunTrace,
  type AccountBasis,
  type G2Position,
  type LedgerEnvelope,
} from "../../api/g2";
import { useStatus } from "../../api/queries";
import { Card, Kpi, Lede, PageHead } from "../../components/Card";
import { Pill } from "../../components/Pill";
import { QueryState } from "../../components/States";
import { Table } from "../../components/Table";
import { num, signedMoney, tone, ts } from "../../lib/format";
import { firstSignalOf } from "../../lib/attribution";
import { BookSelector, InlineError, Missing, useBookParams } from "../orders/Book";

const SIDE_TONE: Record<string, "info" | "warn" | "neutral"> = { LONG: "info", SHORT: "warn", FLAT: "neutral" };

export function PositionsScreen(): React.ReactNode {
  const [book] = useBookParams();
  const status = useStatus();
  const positions = usePositionsBook(book);
  const fills = useFillsBook(book);
  const decisions = useDecisionsBook(book);
  const [pick, setPick] = useState<string | null>(null);

  if (positions.isPending) return <QueryState status="pending" error={null} what="持仓">{null}</QueryState>;
  if (positions.isError) {
    return (
      <>
        <PageHead title="持仓" actions={<BookSelector envelope={null} />} />
        <QueryState status="error" error={positions.error} what="持仓（GET /api/v1/positions）" refetch={() => void positions.refetch()}>
          {null}
        </QueryState>
      </>
    );
  }

  const list = positions.data.rows;
  const open = list.filter((position) => !position.closed);
  const neverRan = !book.run_id && status.isSuccess && status.data.current_run === null;
  const selected = list.find((position) => position.position_id === pick) ?? list[0] ?? null;
  const lifecycle = selected && fills.data ? fills.data.rows.filter((trade) => trade.position_id === selected.position_id) : [];
  const halted = status.data?.halted === true;

  return (
    <>
      <PageHead
        title="持仓"
        sub={
          <>
            {list.length} 个仓位 ·{" "}
            {list.length > 0 && open.length === 0 ? `当前空仓（${list.length} 个已平仓仓位）` : `${open.length} 个仍持有`}
            {halted ? " · 停机中，仓位冻结在最后一次落账" : ""}
          </>
        }
        actions={<BookSelector envelope={positions.data} />}
      />
      <Lede>
        账户盈亏只有一个出处：纸上盘取日报 / 权益序列，回测取运行归档，<b>权益口径为主</b>、现金变动只作对照；
        本屏不把仓位行加总成账户盈亏。表内每个金额列头都标了口径。
      </Lede>

      <AccountBasisRow envelope={positions.data} />

      <div className="grid g23" style={{ marginTop: 12 }}>
        <Card nopad>
          <Table
            cols={[
              { label: "仓位号", mono: true },
              { label: "标的" },
              { label: "方向" },
              { label: "数量", num: true },
              { label: "开仓均价", num: true },
              { label: "平仓均价", num: true },
              { label: "标记价（收盘）", num: true },
              { label: "市值（权益口径）", num: true },
              { label: "未实现（权益口径）", num: true },
              { label: "仓位已实现（内核原值，对照）", num: true },
              { label: "状态" },
              { label: "首笔信号" },
            ]}
            rows={list.map((position) => ({
              key: position.position_id,
              selected: selected?.position_id === position.position_id,
              onClick: () => setPick(position.position_id),
              cells: [
                <span className="mono">{position.position_id}</span>,
                position.instrument_id || "—",
                <Pill tone={SIDE_TONE[position.side] ?? "neutral"}>{position.side}</Pill>,
                num(position.quantity, 0),
                num(position.avg_px_open, 4),
                position.avg_px_close === null ? "—" : num(position.avg_px_close, 4),
                markCell(position, position.mark_px, 4),
                markCell(position, position.market_value, 2, true),
                markCell(position, position.unrealized_pnl, 2, true),
                <span className={tone(position.realized_pnl)}>{signedMoney(position.realized_pnl)}</span>,
                position.closed ? <Pill tone="neutral">已平仓</Pill> : <Pill tone="info">持有中</Pill>,
                <FirstSignal position={position} fills={fills} decisions={decisions} />,
              ],
            }))}
            empty={
              neverRan
                ? "还没有任何运行，本地账没有仓位"
                : "该账没有仓位（从未成交，或成交均已平仓且未保留明细）"
            }
            tall
          />
        </Card>
        <Card title={selected ? `仓位生命周期 · ${selected.position_id}` : "仓位生命周期"} sub="GET /fills · 按 position_id">
          {fills.isError ? (
            <InlineError endpoint="GET /api/v1/fills" error={fills.error} onRetry={() => void fills.refetch()} />
          ) : !selected ? (
            <div className="empty">没有仓位</div>
          ) : lifecycle.length === 0 ? (
            selected.closed ? (
              <div className="note bad">已平仓但没有成交行，账目与成交对不上，<a href="#reconciliation">去对账屏</a></div>
            ) : (
              <div className="empty">该仓位没有可对应的成交明细</div>
            )
          ) : (
            <div className="timeline">
              {lifecycle.map((trade) => (
                <div className={`ti ${trade.side === "BUY" ? "ok" : "warn"}`} key={trade.trade_id}>
                  <span className="when">{ts(trade.ts_event)}</span> {trade.side} {num(trade.last_qty, 0)} @ {num(trade.slipped_px, 4)}{" "}
                  <span className="faint">
                    {trade.trade_id} · 手续费 {num(trade.commission, 2)}
                  </span>
                </div>
              ))}
            </div>
          )}
          {selected?.mark_trade_date ? (
            <div className="hint" style={{ marginTop: 8 }}>
              标记价日期 {selected.mark_trade_date}（纸上盘 positions_marked.json 收盘价）。
            </div>
          ) : null}
        </Card>
      </div>
    </>
  );
}

function markCell(position: G2Position, value: number | null, digits: number, signed = false): React.ReactNode {
  if (value === null) return <Missing why={position.closed ? undefined : "无标记价"} />;
  return signed ? <span className={tone(value)}>{signedMoney(value)}</span> : num(value, digits);
}

function FirstSignal({
  position,
  fills,
  decisions,
}: {
  position: G2Position;
  fills: ReturnType<typeof useFillsBook>;
  decisions: ReturnType<typeof useDecisionsBook>;
}): React.ReactNode {
  if (fills.isError || decisions.isError) return <span className="faint">未取到</span>;
  if (!fills.data || !decisions.data) return <span className="faint">…</span>;
  const signal = firstSignalOf(position.position_id, fills.data.rows, decisions.data.rows);
  return signal ? <a className="mono" href="#attribution">{signal}</a> : <Missing />;
}

/** 账户口径 KPI 行：主数字为权益口径总盈亏，已实现（闭合）与现金变动逐个标注。 */
function AccountBasisRow({ envelope }: { envelope: LedgerEnvelope }): React.ReactNode {
  const paper = envelope.mode === "纸上盘" && envelope.strategy_id && envelope.trade_date;
  const daily = useDailyReportBasis(paper ? envelope.strategy_id : null, paper ? envelope.trade_date : null);
  const fallback = Boolean(paper) && (daily.isError || (daily.isSuccess && daily.data === null));
  const equity = useEquityBasis(fallback ? envelope.strategy_id : null);
  const run = useRunTrace(!paper ? envelope.run_id : null);

  let basis: AccountBasis | null = null;
  let note: React.ReactNode = null;
  let error: React.ReactNode = null;
  if (paper) {
    if (daily.isSuccess && daily.data) basis = daily.data;
    else if (fallback) {
      note = (
        <span className="warn">
          该日日报{daily.isError ? "不存在或文件损坏" : "里没有该策略"}，来源已切换到权益序列
        </span>
      );
      if (equity.isSuccess) basis = equity.data;
      if (equity.isError)
        error = <InlineError endpoint={`GET /api/v1/paper/equity?strategy=${envelope.strategy_id}`} error={equity.error} onRetry={() => void equity.refetch()} />;
    }
  } else if (envelope.run_id) {
    if (run.isSuccess) basis = basisFromRun(run.data);
    if (run.isError) error = <InlineError endpoint={`GET /api/v1/runs/${envelope.run_id}`} error={run.error} onRetry={() => void run.refetch()} />;
  }

  if (error) return error;
  if (!basis) {
    const pending = daily.isFetching || equity.isFetching || run.isFetching;
    return (
      <div className="note">
        {pending ? "取账户口径中…" : "还没有权益口径数字（日报出来前不显示）"}
        {note ? <div>{note}</div> : null}
      </div>
    );
  }
  return (
    <>
      <div className="grid g4">
        <Kpi
          label="总盈亏（权益口径，主）"
          value={basis.total_pnl === null ? <Missing why="旧产物，无权益口径" /> : <span className={tone(basis.total_pnl)}>{signedMoney(basis.total_pnl)}</span>}
          detail={`权益 − 起始资金 · ${basis.source}`}
        />
        <Kpi
          label="已实现（只计闭合仓位）"
          value={basis.realized_closed === null ? <Missing /> : <span className={tone(basis.realized_closed)}>{signedMoney(basis.realized_closed)}</span>}
          detail="realized_pnl_closed · 不是仓位行加总"
        />
        <Kpi
          label="未实现（权益口径）"
          value={basis.unrealized === null ? <Missing why="该出处不给" /> : <span className={tone(basis.unrealized)}>{signedMoney(basis.unrealized)}</span>}
          detail="持仓按收盘价市值"
        />
        <Kpi
          label="现金变动（对照）"
          value={basis.cash_change === null ? <Missing /> : <span className={tone(basis.cash_change)}>{signedMoney(basis.cash_change)}</span>}
          detail="现金口径 · 只作对照"
        />
      </div>
      {basis.legacy ? <div className="hint">旧产物，无权益口径：只显示现金变动对照。</div> : null}
      {note ? <div className="hint">{note}</div> : null}
    </>
  );
}
